/**
 * File: italiansecret-subscription-sticky-cart-bar.js
 * Parallel subscription test clone of italiansecret-sticky-cart-bar.js.
 * It opens the subscription review modal and preserves the production sticky
 * cart bar while Prometheus B2C subscription checkout is tested.
 *
 * - Short item text: "1 item" / "X items"
 * - Never wraps
 * - Button opens Sticky Cart MODAL (not /cart)
 * - Sticky bar auto-refreshes safely
 * - Ensures a valid delivery date exists before opening cart review
 *   (uses the Prometheus-backed calendar API when available)
 * - Added by Italian Secret on 2026-05-11: do not hardcode Tue-Fri when the B2C calendar allows Saturday.
 */

(function () {
  var bar, itemsEl, totalEl, button;
  var POLL_INTERVAL_MS = 2000;

  var DELIVERY_KEY = "italiansecret_delivery_date";
  var OPEN_CART_FLAG = "italiansecret_open_subscription_sticky_cart";

  // Same defaults as your delivery section (can be overridden via data attributes on the bar if you want)
  var DEFAULT_CUTOFF_HOUR = 10;
  var DEFAULT_CUTOFF_MINUTE = 0;

  // Fallback only. The main calendar exposes Prometheus-backed validation when it has loaded.
  function isDeliveryDay(d) {
    var dow = d.getDay();
    return dow === 2 || dow === 3 || dow === 4 || dow === 5 || dow === 6;
  }

  function pad2(n) {
    return String(n).padStart(2, "0");
  }

  function toIso10Local(d) {
    return d.getFullYear() + "-" + pad2(d.getMonth() + 1) + "-" + pad2(d.getDate());
  }

  function parseIso10Local(iso) {
    if (!iso || typeof iso !== "string") return null;
    var m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return null;
    var d = new Date(+m[1], +m[2] - 1, +m[3]);
    if (isNaN(d.getTime())) return null;
    return d;
  }

  function isBeforeDate(a, b) {
    // compares date-only
    var aa = new Date(a.getFullYear(), a.getMonth(), a.getDate()).getTime();
    var bb = new Date(b.getFullYear(), b.getMonth(), b.getDate()).getTime();
    return aa < bb;
  }

  function getCutoffConfig() {
    // Optional: allow bar to define cutoff via data attributes
    // <div data-is-subscription-sticky-cart-bar data-cutoff-hour="10" data-cutoff-minute="0">
    var h = DEFAULT_CUTOFF_HOUR;
    var m = DEFAULT_CUTOFF_MINUTE;

    try {
      if (bar && bar.dataset) {
        if (bar.dataset.cutoffHour) h = parseInt(bar.dataset.cutoffHour, 10);
        if (bar.dataset.cutoffMinute) m = parseInt(bar.dataset.cutoffMinute, 10);
      }
    } catch (e) {}

    if (isNaN(h)) h = DEFAULT_CUTOFF_HOUR;
    if (isNaN(m)) m = DEFAULT_CUTOFF_MINUTE;

    return { hour: h, minute: m };
  }

  function getEarliestAllowedDate() {
    var now = new Date();
    var cfg = getCutoffConfig();

    var cutoffPassed =
      now.getHours() > cfg.hour ||
      (now.getHours() === cfg.hour && now.getMinutes() >= cfg.minute);

    // base is today 00:00
    var base = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    // start from tomorrow; if cutoff passed, start from day after tomorrow
    var start = new Date(base.getFullYear(), base.getMonth(), base.getDate() + 1);
    if (cutoffPassed) start.setDate(start.getDate() + 1);

    return start;
  }

  function getFirstAvailableDeliveryIso() {
    var start = getEarliestAllowedDate();

    // search up to 90 days
    for (var i = 0; i < 90; i++) {
      var d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
      if (isDeliveryDay(d)) return toIso10Local(d);
    }

    // fallback
    return toIso10Local(start);
  }

  function ensureValidDeliveryDateInSession() {
    if (
      window.ItalianSecretDelivery &&
      typeof window.ItalianSecretDelivery.ensureValidIso === "function"
    ) {
      return window.ItalianSecretDelivery.ensureValidIso();
    }

    var storedIso = "";
    try {
      storedIso = sessionStorage.getItem(DELIVERY_KEY) || "";
    } catch (e) {}

    var storedDate = parseIso10Local(storedIso);
    var minDate = getEarliestAllowedDate();

    // invalid/missing or earlier than allowed => overwrite
    if (!storedDate || isBeforeDate(storedDate, minDate) || !isDeliveryDay(storedDate)) {
      var nextIso = getFirstAvailableDeliveryIso();
      try {
        sessionStorage.setItem(DELIVERY_KEY, nextIso);
      } catch (e) {}
      return nextIso;
    }

    return storedIso;
  }

  function persistDeliveryToCartAttribute(iso) {
    // Best-effort: keep Shopify cart attribute aligned (doesn't block UX)
    // Uses /cart/update.js with attributes
    try {
      return fetch("/cart.js", { headers: { Accept: "application/json" }, credentials: "same-origin" })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var curr = (cart && cart.attributes) || {};
          if (curr && typeof curr === "object") {
            curr = Object.assign({}, curr);
            delete curr._agentClientInfo;
            delete curr.agentClientInfo;
          }
          var attributes = Object.assign({}, curr, { "Delivery date": iso || "" });
          return fetch("/cart/update.js", {
            method: "POST",
            headers: { "Content-Type": "application/json", Accept: "application/json" },
            credentials: "same-origin",
            body: JSON.stringify({ attributes: attributes })
          });
        })
        .catch(function () {});
    } catch (e) {
      return Promise.resolve();
    }
  }

  function syncDeliveryToCartLines(iso) {
    if (
      iso &&
      window.ItalianSecretDelivery &&
      typeof window.ItalianSecretDelivery.syncCartLines === "function"
    ) {
      return window.ItalianSecretDelivery.syncCartLines(iso);
    }
    return Promise.resolve();
  }

  function init() {
    bar = document.querySelector('[data-is-subscription-sticky-cart-bar]');
    if (!bar) return;

    itemsEl = bar.querySelector('[data-is-sticky-items]');
    totalEl = bar.querySelector('[data-is-sticky-total]');
    button  = bar.querySelector('[data-is-subscription-sticky-button]');

    if (!itemsEl || !totalEl || !button) return;

    // Review order click
    button.addEventListener("click", function (e) {
      e.preventDefault();
      e.stopPropagation();

      // Ensure a valid date in sessionStorage, autogenerating if needed.
      var iso = ensureValidDeliveryDateInSession();

      // Flag to reopen sticky cart modal after reload
      try {
        sessionStorage.setItem(OPEN_CART_FLAG, "1");
      } catch (err) {}

      Promise.resolve()
        .then(function () { return persistDeliveryToCartAttribute(iso); })
        .then(function () { return syncDeliveryToCartLines(iso); })
        .catch(function () {})
        .then(function () {
          // Reload is required to refresh Liquid cart + modal contents
          window.location.reload();
        });
    });

    fetchAndRender();
    setInterval(fetchAndRender, POLL_INTERVAL_MS);
  }

  function formatMoney(cents) {
    return "\u00a3" + ((cents || 0) / 100).toFixed(2);
  }

  function render(cart) {
    if (!cart || cart.item_count <= 0) {
      bar.hidden = true;
      document.documentElement.classList.remove("is-subscription-sticky-cart-visible");
      return;
    }

    bar.hidden = false;
    document.documentElement.classList.add("is-subscription-sticky-cart-visible");

    itemsEl.textContent =
      cart.item_count === 1 ? "1 item" : cart.item_count + " items";

    totalEl.textContent = formatMoney(cart.total_price);
  }

  function fetchAndRender() {
    fetch("/cart.js", { credentials: "same-origin" })
      .then(function (r) { return r.json(); })
      .then(render)
      .catch(function () {});
  }

  function onCartUpdated(event) {
    if (!bar) {
      init();
      if (!bar) return;
    }
    var cart = event && event.detail ? event.detail.cart : null;
    if (cart && typeof cart === "object") {
      render(cart);
      return;
    }
    fetchAndRender();
  }

  document.documentElement.addEventListener("is:cart:updated", onCartUpdated);
  window.addEventListener("is:cart:updated", onCartUpdated);
  document.addEventListener("cart:updated", onCartUpdated);

  document.addEventListener("DOMContentLoaded", init);
})();
